import React, { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { Button, Flex, DeleteIcon, Input, SelectDropdown } from '../../lib/components'
import { useDebouncedCallback } from '../../lib/utils/hooks'
import { FormValues, MappingRow, SimplifiedFieldMapping } from '../../lib/types'
import { MAPPING_KEY_OPTIONS, SOURCE_OPTIONS } from '../../lib/utils/fieldMapping'

const DynamicFieldMappingEditor = () => {
  const { values, setFieldValue, errors, touched, dirty } = useFormikContext<FormValues>()
  const initialMappings = values?.metadata?.termsheetFieldMapping ?? {}
  const [rows, setRows] = useState<MappingRow[]>([])
  const debouncedSetFieldValue = useDebouncedCallback(setFieldValue, 800)

  useEffect(() => {
    if (rows.length === 0 && Object.keys(initialMappings).length > 0) {
      const arr: MappingRow[] = Object.entries(initialMappings).map(([key, map]) => ({
        id: crypto.randomUUID(),
        keyName: key,
        map: { source: map.source, fieldName: map.fieldName },
      }))
      setRows(arr)
    }
  }, [initialMappings])

  const rowsToRecord = (r: MappingRow[]) => {
    const record: Record<string, SimplifiedFieldMapping> = {}
    r.forEach((row) => {
      if (row.keyName.trim()) {
        record[row.keyName] = {
          source: row.map.source,
          ...(row.map.fieldName?.trim() ? { fieldName: row.map.fieldName.trim() } : {}),
        }
      }
    })
    return record
  }

  const handleAddField = () => {
    const newRow: MappingRow = {
      id: crypto.randomUUID(),
      keyName: '',
      map: { source: '' },
    }
    setRows((prev) => {
      const updated = [...prev, newRow]
      debouncedSetFieldValue('metadata.termsheetFieldMapping', rowsToRecord(updated))
      return updated
    })
  }

  const handleRemoveField = (id: string) => {
    setRows((prev) => {
      const updated = prev.filter((r) => r.id !== id)
      setFieldValue('metadata.termsheetFieldMapping', rowsToRecord(updated))
      return updated
    })
  }

  const handleMappingKeyChange = (id: string, newKey: string) => {
    setRows((prev) => {
      const updated = prev.map((r) =>
        r.id === id
          ? {
              ...r,
              keyName: newKey,
            }
          : r,
      )
      debouncedSetFieldValue('metadata.termsheetFieldMapping', rowsToRecord(updated))
      return updated
    })
  }

  const handleFieldNameChange = (id: string, fieldName: string) => {
    setRows((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, map: { ...r.map, fieldName } } : r))
      debouncedSetFieldValue('metadata.termsheetFieldMapping', rowsToRecord(updated))
      return updated
    })
  }

  const handleSourceChange = (id: string, newSource: string) => {
    setRows((prev) => {
      const updated = prev.map((r) =>
        r.id === id ? { ...r, map: { ...r.map, source: newSource } } : r,
      )
      debouncedSetFieldValue('metadata.termsheetFieldMapping', rowsToRecord(updated))
      return updated
    })
  }

  return (
    <div className='field-container'>
      {rows.length === 0 && (
        <div className='text-gray-500 text-sm mb-4'>No mappings yet. Add one below.</div>
      )}

      {rows.map((row, idx) => (
        <div key={row.id}>
          <div className='form-grid form-grid--cols-4'>
            <div className='field-container'>
              <div className='input-label'>Mapping Key *</div>
              <SelectDropdown
                name={`mapping_${idx}_key`}
                id={`mappingKey_${row.id}`}
                formId=''
                list={MAPPING_KEY_OPTIONS}
                dataTestId={`mapping-key-${idx}`}
                value={row.keyName}
                handleChange={(val: any) => handleMappingKeyChange(row.id, val)}
                dirty={dirty}
                errors={{
                  [`mapping_${idx}_key`]: null,
                }}
                touched={{
                  [`mapping_${idx}_key`]: false,
                }}
              />
            </div>
            <div className='field-container'>
              <div className='input-label'>Source *</div>
              <SelectDropdown
                name={`mapping_${idx}_source`}
                id={`source_${row.id}`}
                formId=''
                list={SOURCE_OPTIONS}
                dataTestId={`mapping-source-${idx}`}
                value={row.map.source || ''}
                handleChange={(val: any) => handleSourceChange(row.id, val)}
                dirty={dirty}
                errors={{
                  [`mapping_${idx}_source`]: null,
                }}
                touched={{
                  [`mapping_${idx}_source`]: false,
                }}
              />
            </div>
            <div className='field-container'>
              <Input
                name={`mapping_${idx}_fieldName`}
                labelText='Template field name'
                placeholder='Same as key'
                dataTestId={`mapping-field-name-${idx}`}
                type='text'
                value={row.map.fieldName ?? ''}
                handleChange={(e) => handleFieldNameChange(row.id, e.target.value)}
                dirty={dirty}
                errors={{ [`mapping_${idx}_fieldName`]: null }}
                touched={{ [`mapping_${idx}_fieldName`]: false }}
              />
            </div>
            <div className='field-container'>
              <Button
                classes={['auto-btn-width', 'sm:mt-36', 'mt-36']}
                variant='secondary-btn'
                IconComponent={<DeleteIcon classes={['button-icon']} />}
                handleClick={() => handleRemoveField(row.id)}
              />
            </div>
          </div>
          {idx === rows.length - 1 && (
            <div className='field-container'>
              <Flex variant='align-center' classes={['ml-4']}>
                <Button
                  classes={['auto-btn-width']}
                  variant='primary-btn'
                  text='+'
                  handleClick={handleAddField}
                />
              </Flex>
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && (
        <Button
          classes={['auto-btn-width']}
          variant='primary-btn'
          text='+'
          handleClick={handleAddField}
        />
      )}
    </div>
  )
}

export default DynamicFieldMappingEditor
